import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import '@fortawesome/fontawesome-free/css/all.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faSearch, faUser, faBell, faSignOut, faBars, faXmark, faGaugeHigh, faComments, faChevronDown, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { onAuthStateChanged } from 'firebase/auth';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const useAuth = () => {
  const logout = () => auth.signOut();
  return {
    user: auth.currentUser,
    logout
  };
};

const SharedHeader = ({ onLogout, userName, environment }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [userState, setUserState] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUserState(currentUser);
        fetchUserProfile(currentUser.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserProfile = async (uid) => {
    try {
      if (!uid) return;
      
      const userDocRef = doc(db, 'pilot', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        setFirstName(userDoc.data().fname || '');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    { label: 'Aircraft', icon: 'fa-light fa-plane', href: '/onboarding#aircraft' },
    { label: 'Certificates & Ratings', icon: 'fa-light fa-file-lines', href: '/onboarding#certificates' },
    { label: 'Medical Certificate', icon: 'fa-light fa-calendar', href: '/onboarding#medical' },
    { label: 'Flight Experience', icon: 'fa-light fa-upload', href: '/onboarding#experience' },
    { label: 'Subscription', icon: 'fa-light fa-credit-card', href: '/subscription' },
    { label: 'Settings', icon: 'fa-light fa-gear', href: '/settings' },
  ];

  return (
    <div className="relative">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-10 z-50">
        <div className="nav-header mx-auto">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-12">
              <Link to="/" className="flex items-center gap-2">
                <div className="bg-aviation-advisor-nav-logo h-12 w-12" />
                <span className="g100 font-urbanist h600 text-gray-100">Aviation Advisor</span>
              </Link>

              <nav className="flex items-center gap-8">
                <Link 
                  to="/dashboard" 
                  className={`nav-link flex items-center gap-2 px-4 py-2 rounded-md ${
                    location.pathname === '/dashboard'
                      ? 'bg-[hsla(85,77%,73%,0.21)] text-gray-100'
                      : 'text-gray-40 hover:text-green-20'
                  }`}                >
                  <FontAwesomeIcon icon={faGaugeHigh} />
                  <span>Dashboard</span>
                </Link>
                <Link 
                  to="/chat" 
                  className={`nav-link flex items-center gap-2 px-4 py-2 rounded-md ${
                    location.pathname === '/chat'
                      ? 'bg-[hsla(85,77%,73%,0.21)] text-gray-100'
                      : 'text-gray-40 hover:text-green-20'
                  }`}
                >
                  <FontAwesomeIcon icon={faComments} />
                  <span>AI Chat</span>
                </Link>
                <Link 
                  to="/knowledge-base" 
                  className={`nav-link flex items-center gap-2 px-4 py-2 rounded-md ${
                    location.pathname === '/knowledge-base'
                      ? 'bg-[hsla(85,77%,73%,0.21)] text-gray-100'
                      : 'text-gray-40 hover:text-green-20'
                  }`}
                >
                  <FontAwesomeIcon icon={faBook} className="inline-block" />
                  <span className="inline-block">Knowledge Base</span>
                </Link>
              </nav>
            </div>

            <div className="flex items-center gap-6">
              <div className="relative w-80">
                <FontAwesomeIcon 
                    icon={faSearch} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input 
                  type="search" 
                  placeholder="Search" 
                  className="input-field w-full pl-10 py-2 pr-4"
                />
              </div>

              <button className="btn-icon-md text-gray-40 hover:text-gray-100">
                <FontAwesomeIcon 
                  icon={faBell} 
                  className="w-6 h-6 text-gray-10 hover:text-gray-100"
                />
              </button>

              {user && (
                <div className="flex items-center gap-3">
                  <span className="p-m text-gray-100">
                    Welcome {firstName ? `${firstName}!` : 'Guest!'}
                  </span>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-2">
                      <img 
                        src="/assets/profile.png" 
                        alt="Profile" 
                        className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                      <FontAwesomeIcon icon={faChevronDown} className="text-gray-40" />
                    </DropdownMenuTrigger>
                    
                    <DropdownMenuContent className="w-48">
                      <DropdownMenuLabel>My Account</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>
                        <Link to="/profile" className="flex w-full">Profile</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Link to="/settings" className="flex w-full">Settings</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Link to="/subscription" className="flex w-full">Subscription</Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <span className="flex items-center gap-2 text-red-600">
                          <FontAwesomeIcon icon={faRightFromBracket} />
                          Sign Out
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <header className="nav-header-mobile md:hidden">
        <div className="flex items-center gap-2">
          <img 
            src="/assets/header-logo.png"
            alt="Aviation Advisor AI"
            className="h-8 w-auto"
          />
        </div>

        <div className="flex items-center gap-4">
          <button className="btn-secondary btn-md relative">
            <i className="fa-light fa-bell" />
          </button>

          <img
            src="/assets/profile.png"
            alt="Profile"
            className="h-8 w-8 rounded-full"
          />

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="btn-secondary btn-md"
          >
            <FontAwesomeIcon 
              icon={isMobileMenuOpen ? faXmark : faBars} 
              className="w-6 h-6"
            />
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="mobile-menu">
            <div className="flex flex-col gap-1">
              <Link
                to="/chat"
                className="nav-link"
              >
                Chat
              </Link>
              {menuItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="nav-link"
                >
                  <FontAwesomeIcon icon={item.iconComponent} className="mr-2" />
                  {item.label}
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="nav-link text-sunrise-60"
              >
                <FontAwesomeIcon icon={faRightFromBracket} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Dropdown Menu */}
      {isMobileMenuOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          <nav className="flex flex-col">
            <Link to="/profile" className="px-4 py-2 text-gray-700 hover:bg-gray-100">
              Profile
            </Link>
            <Link to="/settings" className="px-4 py-2 text-gray-700 hover:bg-gray-100">
              Settings
            </Link>
            <button 
              onClick={() => {
                auth.signOut();
                setIsMobileMenuOpen(false);
              }} 
              className="px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
            >
              Sign Out
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default SharedHeader;